import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ExternalLink, Filter, Phone, Sparkles, Star } from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { PORTFOLIO, PORTFOLIO_CATEGORIES, CONTACT_INFO } from '@/lib/portfolio';

const VALID_SORTS = ['featured', 'newest'];

export default function PortfolioPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read state from URL; fall back to defaults on invalid values
  const rawCat = searchParams.get('cat') || 'All';
  const cat = PORTFOLIO_CATEGORIES.includes(rawCat) ? rawCat : 'All';
  const rawSort = searchParams.get('sort') || 'featured';
  const sort = VALID_SORTS.includes(rawSort) ? rawSort : 'featured';

  const updateParams = (next) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (!value || value === 'All' || value === 'featured') params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params, { replace: false });
  };

  const setCat = (value) => updateParams({ cat: value });
  const setSort = (value) => updateParams({ sort: value });

  const items = useMemo(() => {
    const filtered = cat === 'All' ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === cat);
    if (sort === 'newest') {
      // Sort by addedAt desc; items without addedAt fall to the bottom preserving original order
      const withDate = filtered
        .map((p, idx) => ({ p, idx, t: p.addedAt ? new Date(p.addedAt).getTime() : null }))
        .filter((x) => x.t !== null)
        .sort((a, b) => b.t - a.t)
        .map((x) => x.p);
      const withoutDate = filtered.filter((p) => !p.addedAt);
      return [...withDate, ...withoutDate];
    }
    return filtered;
  }, [cat, sort]);

  return (
    <main data-testid="portfolio-page" className="bg-white">
      <SEO
        title="Portfolio - Website Design & Digital Marketing Case Studies"
        description="Explore 1k+ websites and digital marketing projects by Brand Partners. See real results for pools, healthcare, home services, hospitality & more industries."
        path="/portfolio"
      />

      {/* HERO */}
      <section className="relative pt-14 lg:pt-20 pb-16 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-8">
            <Reveal as="div" className="kicker"><span className="dot"/>Our Portfolio</Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-5 text-[42px] sm:text-[64px] lg:text-[80px]">
              Crafting digital experiences <span className="grad-text">that stand out.</span>
            </Reveal>
          </div>
          <Reveal as="p" delay={140} className="lg:col-span-4 text-[16px] lg:text-[17px] text-bp-slate leading-relaxed">
            From sleek corporate sites to conversion-focused landing pages - our web projects blend design,
            usability, and performance.
          </Reveal>
        </div>

        {/* Mini stat row */}
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 relative">
          {[
            { v: '1k+', l: 'Websites built' },
            { v: '19', l: 'Years experience' },
            { v: '50+', l: 'Industries served' },
            { v: '5.0', l: 'Google rating' },
          ].map((s, i) => (
            <Reveal as="div" key={s.l} delay={i * 60} className="text-center sm:text-left">
              <div className="font-display font-extrabold text-[36px] sm:text-[44px] leading-none tracking-tight grad-text">{s.v}</div>
              <div className="text-[12px] font-medium text-bp-slate-soft mt-2 uppercase tracking-wider">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FILTER TABS */}
      <section className="py-10 lg:py-14 sticky top-[78px] z-20 bg-white/90 backdrop-blur-md border-b border-bp-slate-100">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2 lg:hidden">
              <Filter size={14} className="text-bp-slate-soft"/>
              <span className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft">Filter by industry</span>
            </div>
            <div className="hidden lg:flex items-center gap-2">
              <Filter size={14} className="text-bp-slate-soft"/>
              <span className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft">Browse our work</span>
            </div>
            <div
              className="flex-shrink-0 inline-flex items-center gap-1 p-1 rounded-full bg-bp-slate-50 border border-bp-slate-100"
              role="tablist"
              aria-label="Sort portfolio"
              data-testid="portfolio-sort"
            >
              <button
                type="button"
                onClick={() => setSort('featured')}
                data-testid="portfolio-sort-featured"
                aria-pressed={sort === 'featured'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${
                  sort === 'featured'
                    ? 'bg-white text-bp-ink shadow-sm'
                    : 'text-bp-slate-soft hover:text-bp-ink'
                }`}
              >
                <Star size={13} className={sort === 'featured' ? 'text-bp-blue' : ''}/> Featured
              </button>
              <button
                type="button"
                onClick={() => setSort('newest')}
                data-testid="portfolio-sort-newest"
                aria-pressed={sort === 'newest'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${
                  sort === 'newest'
                    ? 'bg-white text-bp-ink shadow-sm'
                    : 'text-bp-slate-soft hover:text-bp-ink'
                }`}
              >
                <Sparkles size={13} className={sort === 'newest' ? 'text-bp-teal' : ''}/> Recently Added
              </button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="portfolio-filters">
            {PORTFOLIO_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`portfolio-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => setCat(c)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-[13.5px] font-semibold transition-all whitespace-nowrap ${
                  cat === c
                    ? 'bg-bp-ink text-white shadow-md'
                    : 'bg-bp-slate-50 text-bp-slate hover:bg-bp-blue-50 hover:text-bp-blue'
                }`}
              >
                {c}
                {c !== 'All' && <span className="ml-1.5 opacity-60">{PORTFOLIO.filter((p)=>p.category===c).length}</span>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-14 lg:py-20">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {items.map((p, i) => (
              <article
                key={p.url}
                className="card-soft overflow-hidden group flex flex-col"
                data-testid={`portfolio-item-${i}`}
                style={{ animationDelay: `${(i % 9) * 60}ms` }}
              >
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="block relative overflow-hidden">
                  <div className="aspect-[4/3] bg-bp-slate-50 overflow-hidden">
                    <img
                      src={p.img}
                      alt={`${p.name} - website preview`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  {p.addedAt && (Date.now() - new Date(p.addedAt).getTime()) <= 90 * 24 * 60 * 60 * 1000 && (
                    <span
                      data-testid={`portfolio-new-badge-${i}`}
                      className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bp-teal/95 text-white text-[10.5px] font-bold uppercase tracking-[0.14em] shadow-sm backdrop-blur-sm ring-1 ring-white/25"
                      aria-label="Recently added"
                    >
                      <span className="relative inline-flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-75 animate-ping"/>
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white"/>
                      </span>
                      New
                    </span>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-bp-ink/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-bp-ink text-[12px] font-semibold">
                      View project <ExternalLink size={12}/>
                    </span>
                  </div>
                </a>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-bp-blue">{p.category}</div>
                  <h2 className="font-display font-bold text-bp-ink text-[18px] leading-tight mt-1.5">{p.name}</h2>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-bp-slate flex-1">{p.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.services.map((s) => (
                      <span key={s} className="px-2 py-0.5 rounded-full bg-bp-slate-50 text-[11.5px] font-medium text-bp-slate">{s}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          {items.length === 0 && (
            <div className="text-center py-16 text-bp-slate">No projects in this category yet.</div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 text-center">
          <Reveal as="div" className="kicker justify-center"><span className="dot"/>Your story next?</Reveal>
          <h2 className="h-display mt-4 text-[34px] sm:text-[48px]">
            Ready to join our <span className="grad-text">portfolio?</span>
          </h2>
          <p className="mt-5 text-[16px] lg:text-[17px] text-bp-slate max-w-xl mx-auto">
            Let us create a stunning website that drives results for your business.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-primary" data-testid="portfolio-cta-start"><ArrowRight size={16}/> Start Your Project</Link>
            <a href={`tel:${CONTACT_INFO.phoneTel}`} className="btn-secondary"><Phone size={15}/> {CONTACT_INFO.phone}</a>
          </div>
        </div>
      </section>
    </main>
  );
}
